import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createManufacturingLaborBay,
  createManufacturingLaborRateItem,
  deleteManufacturingLaborBay,
  deleteManufacturingLaborRateItem,
  listManufacturingLaborBays,
  listManufacturingLaborRateBayLinks,
  listManufacturingLaborRateItems,
  setManufacturingLaborRateBayLinks,
  updateManufacturingLaborBay,
  updateManufacturingLaborRateItem,
  type MfgLaborBay,
  type MfgLaborBayKind,
  type MfgLaborRateItem,
} from "@/lib/manufacturing/laborBillingClient";

export type LaborBayDraft = {
  bay_code: string;
  name: string;
  kind: MfgLaborBayKind;
  notes: string;
};

export type LaborRateItemDraft = {
  item_code: string;
  label: string;
  flat_rate_usd: string;
  option_code: string;
  roof_eligible: boolean;
  notes: string;
};

const EMPTY_BAY: LaborBayDraft = {
  bay_code: "",
  name: "",
  kind: "builder",
  notes: "",
};

const EMPTY_ITEM: LaborRateItemDraft = {
  item_code: "",
  label: "",
  flat_rate_usd: "0",
  option_code: "",
  roof_eligible: true,
  notes: "",
};

export function useManufacturingLaborBilling(enabled: boolean, navTick: number) {
  const [bays, setBays] = useState<MfgLaborBay[]>([]);
  const [items, setItems] = useState<MfgLaborRateItem[]>([]);
  const [linksByItem, setLinksByItem] = useState<Record<string, string[]>>({});
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [seededDefaults, setSeededDefaults] = useState(false);

  const [showCreateBay, setShowCreateBay] = useState(false);
  const [bayDraft, setBayDraft] = useState<LaborBayDraft>(EMPTY_BAY);
  const [editingBayId, setEditingBayId] = useState<string | null>(null);

  const [showCreateItem, setShowCreateItem] = useState(false);
  const [itemDraft, setItemDraft] = useState<LaborRateItemDraft>(EMPTY_ITEM);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setListBusy(true);
    setLocalError(null);
    const [baysRes, itemsRes, linksRes] = await Promise.all([
      listManufacturingLaborBays(),
      listManufacturingLaborRateItems(),
      listManufacturingLaborRateBayLinks(),
    ]);
    setListBusy(false);

    if (!baysRes.ok) {
      setLocalError(baysRes.error);
      return;
    }
    if (!itemsRes.ok) {
      setLocalError(itemsRes.error);
      return;
    }
    if (!linksRes.ok) {
      setLocalError(linksRes.error);
      return;
    }

    setBays(baysRes.data.bays);
    setSeededDefaults(baysRes.data.seeded_defaults);
    setItems(itemsRes.data.items);

    const map: Record<string, string[]> = {};
    for (const link of linksRes.data.links) {
      const list = map[link.rate_item_id] ?? [];
      list.push(link.bay_id);
      map[link.rate_item_id] = list;
    }
    setLinksByItem(map);
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const orderedBays = useMemo(
    () => [...bays].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [bays]
  );

  function startEditBay(bay: MfgLaborBay) {
    setEditingBayId(bay.id);
    setBayDraft({
      bay_code: bay.bay_code,
      name: bay.name,
      kind: bay.kind,
      notes: bay.notes,
    });
    setShowCreateBay(false);
  }

  function cancelBayForm() {
    setShowCreateBay(false);
    setEditingBayId(null);
    setBayDraft(EMPTY_BAY);
  }

  async function submitBay() {
    const bay_code = bayDraft.bay_code.trim();
    const name = bayDraft.name.trim();
    if (!bay_code || !name) {
      setLocalError("Bay code and name are required.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const r = editingBayId
      ? await updateManufacturingLaborBay({
          bay_id: editingBayId,
          bay_code,
          name,
          kind: bayDraft.kind,
          notes: bayDraft.notes.trim(),
        })
      : await createManufacturingLaborBay({
          bay_code,
          name,
          kind: bayDraft.kind,
          notes: bayDraft.notes.trim(),
        });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    cancelBayForm();
    await reload();
  }

  async function toggleBayActive(bay: MfgLaborBay) {
    setActionBusy(true);
    setLocalError(null);
    const r = await updateManufacturingLaborBay({
      bay_id: bay.id,
      is_active: !bay.is_active,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reload();
  }

  async function removeBay(bayId: string) {
    setActionBusy(true);
    setLocalError(null);
    const r = await deleteManufacturingLaborBay({ bay_id: bayId });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reload();
  }

  function startEditItem(item: MfgLaborRateItem) {
    setEditingItemId(item.id);
    setItemDraft({
      item_code: item.item_code,
      label: item.label,
      flat_rate_usd: String(item.flat_rate_usd),
      option_code: item.option_code,
      roof_eligible: item.roof_eligible,
      notes: item.notes,
    });
    setShowCreateItem(false);
  }

  function cancelItemForm() {
    setShowCreateItem(false);
    setEditingItemId(null);
    setItemDraft(EMPTY_ITEM);
  }

  async function submitItem() {
    const item_code = itemDraft.item_code.trim();
    const label = itemDraft.label.trim();
    const flat_rate_usd = Number(itemDraft.flat_rate_usd);
    if (!item_code || !label) {
      setLocalError("Item code and label are required.");
      return;
    }
    if (!Number.isFinite(flat_rate_usd) || flat_rate_usd < 0) {
      setLocalError("Flat rate must be a number >= 0.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const payload = {
      item_code,
      label,
      flat_rate_usd,
      option_code: itemDraft.option_code.trim(),
      roof_eligible: itemDraft.roof_eligible,
      notes: itemDraft.notes.trim(),
    };
    const r = editingItemId
      ? await updateManufacturingLaborRateItem({ rate_item_id: editingItemId, ...payload })
      : await createManufacturingLaborRateItem(payload);
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    cancelItemForm();
    await reload();
  }

  async function toggleItemActive(item: MfgLaborRateItem) {
    setActionBusy(true);
    setLocalError(null);
    const r = await updateManufacturingLaborRateItem({
      rate_item_id: item.id,
      is_active: !item.is_active,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reload();
  }

  async function removeItem(itemId: string) {
    setActionBusy(true);
    setLocalError(null);
    const r = await deleteManufacturingLaborRateItem({ rate_item_id: itemId });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reload();
  }

  async function setItemBayLinks(rateItemId: string, bayIds: string[]) {
    setActionBusy(true);
    setLocalError(null);
    const r = await setManufacturingLaborRateBayLinks({
      rate_item_id: rateItemId,
      bay_ids: bayIds,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setLinksByItem((prev) => ({ ...prev, [rateItemId]: bayIds }));
  }

  return {
    bays: orderedBays,
    items,
    linksByItem,
    listBusy,
    actionBusy,
    localError,
    seededDefaults,
    showCreateBay,
    setShowCreateBay,
    bayDraft,
    setBayDraft,
    editingBayId,
    startEditBay,
    cancelBayForm,
    submitBay,
    toggleBayActive,
    removeBay,
    showCreateItem,
    setShowCreateItem,
    itemDraft,
    setItemDraft,
    editingItemId,
    startEditItem,
    cancelItemForm,
    submitItem,
    toggleItemActive,
    removeItem,
    setItemBayLinks,
    reload,
  };
}
